import React from 'react';
import * as Sentry from '@sentry/nextjs';
import Page500 from './Page500';
import { detectLocale } from '@/utils/locale';

function isChunkLoadError(error) {
  return (
    error?.name === 'ChunkLoadError' ||
    error?.message?.includes('Loading chunk') ||
    error?.message?.includes('Loading CSS chunk') ||
    error?.message?.includes('Failed to fetch dynamically imported module')
  );
}

const MAX_CHUNK_RETRIES = 3;
const RETRY_DELAY_MS = 1500;

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isChunkError: false,
      chunkRetryCount: 0,
      isRetrying: false
    };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error,
      isChunkError: isChunkLoadError(error)
    };
  }

  retryChunkLoad = () => {
    this.setState((prevState) => ({
      hasError: false,
      error: null,
      errorInfo: null,
      isChunkError: false,
      chunkRetryCount: prevState.chunkRetryCount + 1,
      isRetrying: false
    }));
  };

  componentDidCatch(error, errorInfo) {
    // Auto-retry chunk load errors
    if (isChunkLoadError(error) && this.state.chunkRetryCount < MAX_CHUNK_RETRIES) {
      console.warn(`ChunkLoadError detected, retry attempt ${this.state.chunkRetryCount + 1}/${MAX_CHUNK_RETRIES}`);
      this.setState({ isRetrying: true });
      setTimeout(this.retryChunkLoad, RETRY_DELAY_MS);
      return;
    }
    // Log error details
    const errorDetails = {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      errorType: error.constructor.name,
      // Additional context
      userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : 'server',
      url: typeof window !== 'undefined' ? window.location.href : 'server'
    };

    // Log to console in development
    if (process.env.NODE_ENV !== 'production') {
      console.error('ErrorBoundary caught an error:', errorDetails);
    }

    // Log to server-side logger if available
    if (typeof window === 'undefined') {
      const logger = require('@/utils/logger.server').default;
      logger.error('React Error Boundary caught error', errorDetails);
    }

    // Send to Sentry
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack
        }
      }
    });

    // Store error info in state
    this.setState({
      errorInfo
    });
  }

  render() {
    const { hasError, isRetrying, isChunkError, chunkRetryCount, error, errorInfo } = this.state;
    const isDevelopment = process.env.NODE_ENV !== 'production';
    const { locale } = detectLocale(typeof window !== 'undefined' ? window.location.pathname : '/');
    const isCs = locale !== 'pl';

    // Show loading state during chunk retry
    if (isRetrying) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
          <div className="max-w-md text-center">
            <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
            <p className="text-gray-600">
              {isCs ? 'Načítání' : 'Ładowanie'}… ({isCs ? 'pokus' : 'próba'} {chunkRetryCount + 1}/{MAX_CHUNK_RETRIES})
            </p>
          </div>
        </div>
      );
    }

    if (hasError) {
      // Chunk error after all retries exhausted - offer full reload
      if (isChunkError && !isDevelopment) {
        return (
          <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
            <div className="max-w-md text-center">
              <h1 className="mb-4 text-2xl font-semibold text-gray-800">{isCs ? 'Nastala chyba' : 'Wystąpił błąd'}</h1>
              <p className="mb-6 text-gray-600">
                {isCs
                  ? 'Při načítání stránky došlo k chybě. Zkuste to prosím znovu.'
                  : 'Podczas ładowania strony wystąpił błąd. Spróbuj ponownie.'}
              </p>
              <button
                onClick={() => window.location.reload()}
                className="inline-block rounded bg-blue-600 px-6 py-3 text-white transition-colors hover:bg-blue-700"
              >
                {isCs ? 'Zkusit načíst znovu' : 'Spróbuj ponownie'}
              </button>
            </div>
          </div>
        );
      }

      // In development, show detailed error with component stack
      if (isDevelopment && error && errorInfo) {
        return (
          <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
            <div className="w-full max-w-4xl">
              <div className="rounded-lg bg-white p-8 shadow-lg">
                <div className="mb-6 text-center">
                  <h1 className="mb-2 text-4xl font-bold text-red-600">React Error</h1>
                  <h2 className="mb-4 text-2xl font-semibold text-gray-800">Chyba při renderování komponenty</h2>
                </div>

                <div className="mb-6">
                  <h3 className="mb-2 font-semibold text-gray-700">Chybová zpráva:</h3>
                  <div className="rounded bg-red-50 p-4">
                    <code className="text-sm text-red-800">{error.toString()}</code>
                  </div>
                </div>

                {error.stack && (
                  <div className="mb-6">
                    <h3 className="mb-2 font-semibold text-gray-700">Error stack:</h3>
                    <div className="max-h-64 overflow-auto rounded bg-gray-900 p-4">
                      <pre className="text-xs text-gray-100">{error.stack}</pre>
                    </div>
                  </div>
                )}

                {errorInfo.componentStack && (
                  <div className="mb-6">
                    <h3 className="mb-2 font-semibold text-gray-700">Component stack:</h3>
                    <div className="max-h-64 overflow-auto rounded bg-gray-900 p-4">
                      <pre className="text-xs text-yellow-100">{errorInfo.componentStack}</pre>
                    </div>
                  </div>
                )}

                <div className="mt-6 rounded bg-yellow-50 p-4">
                  <p className="text-sm text-yellow-800">
                    <strong>Development mode:</strong> Tato detailní chybová zpráva se zobrazuje pouze ve vývojovém
                    režimu. V produkci uživatelé uvidí pouze generickou chybovou stránku.
                  </p>
                </div>

                <div className="mt-6 text-center">
                  <button
                    onClick={() => window.location.reload()}
                    className="inline-block rounded bg-blue-600 px-6 py-3 text-white transition-colors hover:bg-blue-700"
                  >
                    Znovu načíst stránku
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      }

      // In production, show generic error page
      return (
        <Page500 error={isCs ? 'Při renderování stránky došlo k chybě' : 'Podczas renderowania strony wystąpił błąd'} />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
